import {ReactNode} from 'react';

import {
  EMPTY_SEARCH_RESULT_PROPS,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult} from 'notebook/mediocre/search/SearchResults';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
  TooltipIcon,
} from 'notebook/tables/constants/TableColumnConstants';
import {TABLE_SEARCH_PATHS} from 'notebook/tables/constants/TableConstants';
import {TABLE_COLUMN, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeCanDeleteColumn,
  computeCanInsertColumn,
  computeColumnHeaderIconPrefix,
  computeColumnIsOutdated,
  computeHasDestinationColumnMapping,
  computeIsSingleInputColumn,
  computeShouldHighlightColumnHeader,
  InsertDirection,
} from 'notebook/tables/store/logic/ColumnHeaderLogic';
import {UNAVAILABLE_TABLE_INTERACTIONS} from 'notebook/tables/store/logic/interactions/createTableInteractions';
import {UNAVAILABLE_TABLE_MUTATIONS} from 'notebook/tables/store/logic/mutations/createTableMutations';
import {getTableCellAffixedProps} from 'notebook/tables/store/logic/TableAffixedLogic';
import {
  AxisCellAffixedLayout,
  AxisHeaderModel,
  createAxisHeaderIdentitySelector,
  createAxisIsProtectedSelector,
  createAxisIsSelectedSelector,
  createAxisIsTableSelectedSelector,
  createAxisSelectionRangeSelector,
  createAxisShowExcessLabelsSelector,
  UNAFFIXED_LAYOUT,
} from 'notebook/tables/store/models/AxisHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {getSubtreeAtIndex, MaybeIndexTree} from 'util/IndexTree';

/**
 * The model for a single column header cell, keyed by column index. Rendered by `TableHeaderRow`
 * for regular columns and by `TableSingleInput` for Single Input columns.
 *
 * This is the per-column counterpart to `RowModel`. Not to be confused with `TableColumnHeaderModel`
 * (`TableModel.columnHeader`), which holds the table-level configuration that every column header
 * shares.
 */
export interface ColumnHeaderModel extends AxisHeaderModel {
  dim: TableDimension.COLUMN;
  /**
   * Whether the column can be renamed. Only the column axis can be renamed, so this has no row-axis
   * counterpart.
   */
  canRenameDim: boolean;
  /** The editable name of the column, which renaming starts from. */
  label: string | null;
  /** The icon rendered before the label, or null when the column has no prefix icon. */
  iconPrefix: BenchlingIconName | null;
  /**
   * Icons rendered after the label, whose tooltips are also folded into the cell's tooltip. Only
   * supplied by standalone column headers; in-table columns take these from their column constraint.
   */
  tooltipIcons: Array<TooltipIcon> | null;
  /** Whether the header is highlighted as a user-added custom column. */
  shouldHighlightCell: boolean;
  /** Whether the column is backed by an outdated schema field. */
  columnIsOutdated: boolean;
  /**
   * The column's constraint, or null when the column has none (for example a plain table column or
   * a ghost cell past the end of the table).
   */
  columnConstraint: ColumnConstraint | null;
  /**
   * The document search matches that fall on this header, which tint the cell when the ctrl-F
   * search highlights it. Only column headers are searchable, so the row axis has no counterpart.
   */
  searchResultProps: MediocreSearchResultProps;
  /**
   * Whether the table only accepts the columns it offers in {@link insertableColumnInfos}, rather
   * than freeform custom columns. Such a table inserts one column at a time, so its header's insert
   * options read "Insert column left" even while several columns are selected.
   */
  disallowCustomColumns: boolean;
  /**
   * The columns offered by the header's "Insert column left/right" submenus, or undefined when the
   * table offers none. A Single Input header offers only the Single-Input-compatible subset.
   */
  insertableColumnInfos: Array<InsertableColumnInfo> | undefined;
  /** The tooltip explaining why inserting is disabled, or undefined when the table gives no reason. */
  disableInsertDimTooltip: string | undefined;
  /** Whether the header's "Lock column" context menu option is suppressed. */
  disableColumnLocking: boolean;
  /**
   * Whether this header labels a Single Input column, which is rendered by `TableSingleInput`
   * outside the scrolling table. Such a header styles itself differently and names its insert
   * options after single inputs. Only the column axis has Single Inputs, so this has no row-axis
   * counterpart.
   */
  isSingleInputHeader: boolean;
  /**
   * Whether the table lives in a configuration document (a template). Locking and unlocking a
   * column is configuration, so outside a template it is offered only to tenant admins with
   * `ELN_ALLOW_ADMINS_TO_EDIT_COLUMN_LOCKING_IN_EXECUTION`. Only the column axis can lock, so this
   * has no row-axis counterpart.
   */
  isConfig: boolean;
  /**
   * Whether the document the table lives in lets users freeze and unfreeze columns, which gates the
   * header's "Freeze up to this column" / "Unfreeze from this column" options. Only the column axis
   * can be frozen, so this has no row-axis counterpart.
   */
  canFreezeColumns: boolean;
}

/**
 * Resolves the search matches on the column header at `index` from a table-level search tree, which
 * indexes its matches by `tableHeaderRow` and then by column.
 */
function columnHeaderSearchSubtree(
  tree: MaybeIndexTree<MediocreSearchResult> | undefined,
  index: number
): MaybeIndexTree<MediocreSearchResult> {
  return getSubtreeAtIndex(getSubtreeAtIndex(tree ?? null, TABLE_SEARCH_PATHS.TABLE_HEADER_ROW), index);
}

/**
 * Creates a selector for the {@link AxisCellAffixedLayout} of the column header at `index`. The
 * column is placed within the frozen columns by {@link getTableCellAffixedProps}, the same
 * derivation the data cells in that column use. Single Input columns are rendered outside the
 * scrollable table, so they are never affixed.
 */
function createColumnHeaderAffixedLayoutSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => AxisCellAffixedLayout {
  return createTableStoreSelector(
    'columnHeaderAffixedLayout',
    {
      affixedColumnCount: (store) => tableSelector(store).affixed.affixedColumnCount,
      hasAnyFrozenColumns: (store) => tableSelector(store).affixed.hasAnyFrozenColumns,
      showAffixedShadow: (store) => tableSelector(store).affixed.showAffixedShadow,
      firstColumnWidth: (store) => tableSelector(store).measurements.firstColumnWidth,
      columnWidths: (store) => tableSelector(store).measurements.columnWidths,
      sliceStart: (store) => tableSelector(store).singleInputs.slices.start,
      singleInputBoundaryIndex: (store) => tableSelector(store).singleInputs.boundaryIndex,
    },
    ({singleInputBoundaryIndex, ...affixedConfig}): AxisCellAffixedLayout => {
      if (computeIsSingleInputColumn(index, singleInputBoundaryIndex)) {
        return UNAFFIXED_LAYOUT;
      }
      const affixed = getTableCellAffixedProps({j: index, ...affixedConfig});
      return {
        isAffixed: Boolean(affixed.isAffixed),
        affixedLeftSpacing: affixed.affixedLeftSpacing ?? 0,
        showAffixedShadow: Boolean(affixed.showAffixedShadow),
        isLastFrozenColumn: Boolean(affixed.isLastFrozenColumn),
        // The leading-column treatment belongs to the row axis; a column header is never the
        // table's leading column.
        isFirstColumn: false,
      };
    }
  );
}

/** Creates a selector for the column header at `index` against the table store. */
export function createColumnHeaderModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => ColumnHeaderModel {
  const columnConstraintSelector = (store: TableEditableStore): ColumnConstraint | null =>
    store.volatileProps.table.columnConstraints?.[index] ?? null;

  // Each input is a value (or a stable object off table props) rather than the table itself, so
  // that editing an unrelated part of the table does not invalidate every column header's model.
  return createTableStoreSelector(
    'columnHeaderModel',
    {
      identity: createAxisHeaderIdentitySelector(tableSelector, TABLE_COLUMN, index),
      selectionRange: createAxisSelectionRangeSelector(tableSelector, TABLE_COLUMN, index),
      isSelected: createAxisIsSelectedSelector(tableSelector, TABLE_COLUMN, index),
      isTableSelected: createAxisIsTableSelectedSelector(tableSelector, TABLE_COLUMN),
      affixedLayout: createColumnHeaderAffixedLayoutSelector(tableSelector, index),
      columnConstraint: columnConstraintSelector,
      // Narrowed to this column's own subtree in the input rather than in the combiner. A search
      // rebuilds the whole tree on every keystroke, so taking the table-level tree as the input
      // would re-run the combiner (and re-render every column header) whenever the search matched
      // anything in the table. Subtree nodes are stable references, and a column the search never
      // matched reads `null` both times, so the model only changes when this column's matches do.
      searchResultsTree: (store) => columnHeaderSearchSubtree(store.stableProps.searchResultsTree, index),
      selectedSearchResultTree: (store) =>
        columnHeaderSearchSubtree(store.stableProps.selectedSearchResultTree, index),
      showExcessLabels: createAxisShowExcessLabelsSelector(TABLE_COLUMN),
      customLabel: (store) => TableUtil.getRenderedColumnLabel(store.volatileProps.table, index),
      label: (store) => TableUtil.getColumnLabel(store.volatileProps.table, index),
      tableKind: (store) => TableUtil.getTableKind(store.volatileProps.table),
      singleInputBoundaryIndex: (store) => tableSelector(store).singleInputs.boundaryIndex,
      highlightCustomColumns: (store) => store.editorContext.highlightCustomColumns,
      hideColumnHeaderIcons: (store) => store.stableProps.hideColumnHeaderIcons,
      columnCount: (store) => tableSelector(store).dimensions.columnCount,
      columnConstraints: (store) => store.volatileProps.table.columnConstraints,
      legacyFirstEditableColumnIndex: (store) =>
        TableUtil.legacyFirstEditableColumnIndex(store.volatileProps.table) ?? null,
      disallowCustomColumns: (store) => TableUtil.disallowCustomColumns(store.volatileProps.table),
      allowColumnEdits: (store) => TableUtil.allowColumnEdits(store.volatileProps.table),
      disableInsertDimTooltip: (store) => store.volatileProps.table._disableInsertColsTooltip,
      canAddRemoveColumns: (store) => tableSelector(store).columnHeader.canAddRemoveColumns,
      locked: (store) => tableSelector(store).columnHeader.locked,
      disableLegacyHeader: (store) => tableSelector(store).columnHeader.disableLegacyHeader,
      allowLookupColumnEdits: (store) => Boolean(store.volatileProps.table.allowLookupColumnEdits),
      canDeleteTable: (store) => tableSelector(store).canDeleteTable,
      // `props` holds TableEditable's raw props, so the `??` default here repeats the one
      // `TableEditable`'s `propsWithDefaults` applies before rendering the class component.
      disableColumnLocking: (store) => store.stableProps.disableColumnLocking ?? false,
      isProtected: createAxisIsProtectedSelector(tableSelector),
      isConfig: (store) => tableSelector(store).context.isConfigTable,
      canFreezeColumns: (store) => tableSelector(store).columnHeader.supportsUserFreezeColumns,
      viewer: (store) => tableSelector(store).columnHeader.viewer,
      isLeftAlign: (store) => tableSelector(store).columnHeader.isLeftAlignColumnHeader,
      shouldShowColumnDragHandles: (store) => tableSelector(store).reordering.shouldShowColumnDragHandles,
      insertableColumnInfos: (store) => tableSelector(store).columnHeader.insertableColumnInfos,
      // Single Input headers offer only the columns that can themselves be configured as Single
      // Inputs, so they insert from their own subset of the table's insertable columns.
      singleInputInsertableColumnInfos: (store) =>
        tableSelector(store).columnHeader.singleInputInsertableColumnInfos,
      // The column mapping lookup reads global mappings state, so it is an input rather than part
      // of the memoized result.
      hasDestinationColumnMapping: (store) =>
        computeHasDestinationColumnMapping(
          store.stableProps.fileId,
          store.volatileProps.table,
          columnConstraintSelector(store)
        ),
      mutations: (store) => tableSelector(store).mutations,
      interactions: (store) => tableSelector(store).interactions,
    },
    ({
      identity,
      selectionRange,
      isSelected,
      isTableSelected,
      affixedLayout,
      columnConstraint,
      searchResultsTree,
      selectedSearchResultTree,
      showExcessLabels,
      customLabel,
      label,
      tableKind,
      singleInputBoundaryIndex,
      highlightCustomColumns,
      hideColumnHeaderIcons,
      hasDestinationColumnMapping,
      columnCount,
      columnConstraints,
      legacyFirstEditableColumnIndex,
      disallowCustomColumns,
      allowColumnEdits,
      disableInsertDimTooltip,
      canAddRemoveColumns,
      locked,
      disableLegacyHeader,
      allowLookupColumnEdits,
      canDeleteTable,
      disableColumnLocking,
      isProtected,
      isConfig,
      canFreezeColumns,
      viewer,
      insertableColumnInfos,
      singleInputInsertableColumnInfos,
      isLeftAlign,
      shouldShowColumnDragHandles,
      mutations,
      interactions,
    }): ColumnHeaderModel => {
      const columnDefinition = columnConstraint?.columnDefinition ?? null;
      const isSingleInputColumn = computeIsSingleInputColumn(index, singleInputBoundaryIndex);
      const insertableColumns = isSingleInputColumn
        ? singleInputInsertableColumnInfos
        : insertableColumnInfos;
      // A ghost cell past the last column labels no real column, so it is never editable.
      const isColumnEditable = !viewer && !locked && allowColumnEdits && index < columnCount;
      // A dedicated lookup column can be deleted even where columns are otherwise not editable,
      // when the table opts into lookup column edits. Single Input headers are not offered this:
      // `TableSingleInput` only ever allowed deleting an editable column.
      const isDeletableLookupColumn =
        !isColumnEditable &&
        !isSingleInputColumn &&
        index < columnCount &&
        !viewer &&
        !locked &&
        allowLookupColumnEdits &&
        columnDefinition?.type === TableColumnType.DEDICATED_LOOKUP;
      const canInsert = (direction: InsertDirection): boolean =>
        isColumnEditable &&
        computeCanInsertColumn({
          columnConstraints,
          legacyFirstEditableColumnIndex,
          disallowCustomColumns,
          canAddRemoveColumns,
          insertableColumnInfos: insertableColumns,
          selectionRange,
          direction,
        });
      return {
        ...identity,
        mutations,
        interactions,
        dim: TABLE_COLUMN,
        isHeaderCell: true,
        isLeftAlign,
        showDragHandle: !identity.isGhostCell && shouldShowColumnDragHandles,
        isSingleInputHeader: isSingleInputColumn,
        customLabel,
        selectionRange,
        isSelected,
        isTableSelected,
        affixedLayout,
        shouldDisableDeleteDim: !computeCanDeleteColumn({
          columnConstraints,
          legacyFirstEditableColumnIndex,
          canAddRemoveColumns,
          selectionRange,
        }),
        shouldDisableInsertDimBefore: !canInsert('BEFORE'),
        shouldDisableInsertDimAfter: !canInsert('AFTER'),
        isLocked: locked,
        isProtected,
        canRenameDim: isColumnEditable,
        canToggleHeader: isColumnEditable && index === 0 && !disableLegacyHeader,
        canInsertDims: isColumnEditable,
        canDeleteDims: isColumnEditable || isDeletableLookupColumn,
        canDeleteTable,
        disableColumnLocking,
        isConfig,
        canFreezeColumns,
        showExcessLabels,
        label,
        columnConstraint,
        searchResultProps: {searchResultsTree, selectedSearchResultTree},
        disallowCustomColumns,
        insertableColumnInfos: insertableColumns,
        disableInsertDimTooltip,
        iconPrefix: computeColumnHeaderIconPrefix({
          hideColumnHeaderIcons,
          hasDestinationColumnMapping,
          columnDefinition,
        }),
        tooltipIcons: null,
        shouldHighlightCell: computeShouldHighlightColumnHeader({
          isSingleInputColumn,
          highlightCustomColumns,
          columnDefinition,
          tableKind,
        }),
        columnIsOutdated: computeColumnIsOutdated(columnDefinition),
      };
    }
  );
}

/**
 * A {@link ColumnHeaderModel} for a column header rendered outside of any table store (for example
 * `SimpleTableAxisCell`). Such headers only ever label real columns, so they are never ghost cells
 * and never selected, and their label and icons come directly from their props rather than from a
 * table.
 */
export function makeStandaloneColumnHeaderModel({
  index,
  customLabel = null,
  label = null,
  tooltipIcons = null,
  numDims = 0,
  isLeftAlign = false,
}: {
  index: number;
  customLabel?: ReactNode;
  label?: string | null;
  tooltipIcons?: Array<TooltipIcon> | null;
  numDims?: number;
  isLeftAlign?: boolean;
}): ColumnHeaderModel {
  return {
    dim: TABLE_COLUMN,
    index,
    isGhostCell: false,
    numDims,
    isHeaderCell: true,
    isLeftAlign,
    showDragHandle: false,
    isSingleInputHeader: false,
    customLabel,
    selectionRange: null,
    isSelected: false,
    isTableSelected: false,
    affixedLayout: UNAFFIXED_LAYOUT,
    shouldDisableDeleteDim: true,
    shouldDisableInsertDimBefore: true,
    shouldDisableInsertDimAfter: true,
    isLocked: false,
    isProtected: false,
    canRenameDim: false,
    canToggleHeader: false,
    canInsertDims: false,
    canDeleteDims: false,
    canDeleteTable: false,
    disableColumnLocking: false,
    isConfig: false,
    canFreezeColumns: false,
    showExcessLabels: false,
    label,
    columnConstraint: null,
    searchResultProps: EMPTY_SEARCH_RESULT_PROPS,
    disallowCustomColumns: false,
    insertableColumnInfos: undefined,
    disableInsertDimTooltip: undefined,
    iconPrefix: null,
    tooltipIcons,
    shouldHighlightCell: false,
    columnIsOutdated: false,
    mutations: UNAVAILABLE_TABLE_MUTATIONS,
    interactions: UNAVAILABLE_TABLE_INTERACTIONS,
  };
}
